import { pluralize, popRootKey } from '../../util'
import env from '../../env'
import _ from 'underscore'
import { differenceInMilliseconds } from 'date-fns'

import subscribe from '@polleverywhere/firehose_client'

function addSubscription (path, opts) {
  const subscriptionChannel = subscribe(path, {
    host: env.get('firehose_url').toString(),
    ssl: env.get('firehose_ssl'),
    params: (typeof opts.params === 'function' ? opts.params() : opts.params),
    preflight: this.livePreflight ? this.livePreflight : undefined
  })
  if (opts.message) subscriptionChannel.on('message', opts.message)
  if (opts.failed) subscriptionChannel.on('failed', opts.failed)
  if (opts.error) subscriptionChannel.on('error', opts.error)
  if (opts.connected) subscriptionChannel.on('up', opts.connected)
  if (opts.disconnected) subscriptionChannel.on('down', opts.disconnected)

  this._subscriptionChannels.push(subscriptionChannel)
}

export default function (self) {
  // Store of what we've subscribed to
  self._subscriptionChannels = []

  return {
    /**
     * @deprecated Use #live
     */
    firehoseConnect (opts) {
      return self.live(opts)
    },

    /**
     * @deprecated Use #live
     */
    firehoseConnectSingle (opts) {
      return self.live(opts)
    },

    /**
     * @deprecated Use #live
     */
    firehoseConnectMultiplexed (opts = {}) {
      return self.live(opts)
    },

    /**
     * @deprecated Use #die
     */
    firehoseDisconnect () {
      self.die()
    },

    /**
     * Auto-connect to a polls firehose channel
     * Delegates to #live
     */
    livePoll (opts = {}) {
      if (!self.type()) { return }
      const pluralizedType = pluralize(2, self.type())
      opts.path = `/${pluralizedType}/${self.get('permalink')}.json`
      return self.live(opts)
    },

    /**
     * Subscribe to Firehose channels to receive live updates.
     *
     * If you want to do more complex things, use the subscribe method from
     * @polleverywhere/firehose_client directly
     *
     * @param {Object} opts Configurable options
     * @param {string|string[]} opts.path A single or array list of strings
     *    containing Firehose channels as relative paths to subscribe to.
     * @param {Object} opts.params Request params to attach to WS send messages
     * @param {function} opts.message Incoming message response handler
     * @param {function} opts.connected Handler called when Firehose connects
     * @param {function} opts.disconnected Handler called when Firehose disconnects
     * @param {function} opts.error Handler called when Firehose errors
     * @param {function} opts.failed Handler called when Firehose fails
     * @param {function} opts.subscriptionFailed Handler called when
     *    subscription to Firehose channels fail
     */
    live (opts = {}) {
      opts = _.defaults({}, opts, {
        message (firehosePayload) {
          self._handleLiveMessage(firehosePayload, opts)
        },
        path: _.result(self, 'liveChannel'),
        params: self.liveParams
      })

      let channelPaths = opts.path
      if (!Array.isArray(channelPaths)) {
        channelPaths = [channelPaths]
      }

      channelPaths.forEach((channelPath) => addSubscription.call(self, channelPath, opts))
    },

    _handleLiveMessage (firehosePayload, opts = {}) {
      if (!self.silenced) {
        // Before delegating responses to each activity's processor, standardize
        // the structure to be flat without root keys. To support both responses
        // with and without root keys, only pop off poll type root keys, which
        // will no longer exist once all activities are switched over to
        // shared/instance channels.
        const data = typeof this.type === 'function' && this.type() in firehosePayload
          ? popRootKey(firehosePayload) : firehosePayload

        if (self.id === data.id) {
          const canCompareDates = self.has('updated_at') && data.updated_at
          const isNewerMessage = !canCompareDates ||
            differenceInMilliseconds(new Date(data.updated_at),
              new Date(self.get('updated_at'))) >= 0

          // Only process live responses that are newer than when the model was
          // last updated. Otherwise, do nothing.
          if (!isNewerMessage) { return }
        }

        // Each model implements processLiveResponse
        if (typeof self.processLiveResponse === 'function') {
          self.processLiveResponse(data, opts)
        }

        /*
         * Trigger a live response event so 3rd party views can access data
         * coming down from firehose
         */
        self.trigger('liveResponse', data)
      }
    },

    /**
     * Disconnect from all channels
     */
    die () {
      self._subscriptionChannels.forEach((channel) => channel.unsubscribe())
      self._subscriptionChannels = []
    },

    /**
     * Silence and ignore updates from firehose
     */
    silence (flag = true) {
      self.silenced = flag === true
    }
  }
}
